import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AnalyzeResourceResponseBodyType } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { UseQueryResult } from "@tanstack/react-query";

import {
  useDashboardExperienceOrDefault,
  useExperimentAnalysis,
} from "../../../../api/analysis/analysis";
import { useGetExperiencesList } from "../../../../api/experiences-list/get-experiences-list";
import {
  activeExperiencesFilter,
  filterDashboardData,
} from "../../../../api/formatters/dashboard";
import {
  NoExperimentDataOverlay,
  NoExperimentOverlay,
} from "../../../../components/NoDataOverlay/NoExperimentDataOverlay";
import {
  initEnsuredExperienceFromDto,
  selectedExperienceState,
} from "../../../../store/slices/experiences";
import { initVariationsState } from "../../../../store/slices/variations";
import { IAppState } from "../../../../store/types/store";
import Loading from "../../../../utils/Loading";
import { DashboardHeader } from "../../DashboardHeader";
import { DashboardHeaderSimple } from "../../DashboardHeaderSimple";
import { dataReconciliationGraphsConfig } from "../../Graphs/Utils/graphConfigs";
import {
  hasFiltersBeenModified,
  sortExclusionReasons,
} from "../../Utils/utils";

import { DataReconciliationTable } from "./DataReconciliationTable";

export const DataReconciliationDashboard = () => {
  const experienceList = useGetExperiencesList({ category: "experiment" });

  const dashboardData: UseQueryResult<AnalyzeResourceResponseBodyType>[] =
    useExperimentAnalysis(dataReconciliationGraphsConfig);
  const experience = useDashboardExperienceOrDefault(undefined);

  const dispatch = useDispatch();
  const experienceState = useSelector(selectedExperienceState);

  const dashboardFilters = useSelector(
    (state: IAppState) => state.dashboardFilters
  );

  const shouldNotRenderGraphs = useMemo(() => {
    return (
      dashboardData &&
      dashboardData[0].isSuccess &&
      dashboardData[0].data?.metrics.length < 1 &&
      !hasFiltersBeenModified(dashboardFilters)
    );
  }, [dashboardData, dashboardFilters]);

  const tableData = useMemo(() => {
    return filterDashboardData(dashboardData, "ordersReconciliation");
  }, [dashboardData]);

  useEffect(() => {
    if (experience.isSuccess) {
      dispatch(
        initEnsuredExperienceFromDto({ experience: experience.data.experience })
      );
      dispatch(
        initVariationsState({
          variations: experience.data.experience.variations,
        })
      );
    }
  }, [experience.isSuccess]);

  if (
    !dashboardFilters.experienceId &&
    experienceState.isSuccess &&
    experience.isSuccess
  ) {
    return <NoExperimentOverlay />;
  }
  if (
    experienceState.isSuccess &&
    experienceState.experience &&
    experienceList.isSuccess &&
    dashboardFilters.experienceId &&
    experience.isSuccess &&
    experience.data.experience
  ) {
    return (
      <Grid container direction={"column"} rowSpacing={3}>
        {shouldNotRenderGraphs ? (
          <DashboardHeaderSimple
            latestRunTs={dashboardData[0]?.data?.latestRunTs}
            activeExperiences={activeExperiencesFilter(
              experienceList.data.experiencesList
            )}
          />
        ) : (
          <DashboardHeader
            activeExperiences={activeExperiencesFilter(
              experienceList.data.experiencesList
            )}
            pageName={"dataReconciliation"}
            latestRunTs={dashboardData[0]?.data?.latestRunTs}
          />
        )}
        {shouldNotRenderGraphs ? (
          <NoExperimentDataOverlay />
        ) : (
          <Grid item>
            <DataReconciliationTable
              data={tableData ? sortExclusionReasons(tableData) : undefined}
            />
          </Grid>
        )}
      </Grid>
    );
  }
  return <Loading />;
};
